import {
  observable,
  action,
  runInAction,
  makeObservable,
  computed,
} from "mobx";
import PopupElement from "./models/PopupElement";
import getCookie from "../utils/getCookie";
import setCookie from "../utils/setCookie";

import { MAIN_PAGE_QUERY_PARAM } from "../consts";

const COOKIE_BANNER_FEATURE_ID = 135;

class Header {
  /** Открыть меню, в котором информация о пользователе */
  showMenu = false;

  searchOverlay = false;

  /** Открыть подменю, в котором ссылки на разные разделы сайта */
  showSubmenu = false;

  showUserMenuWhenSticky = false;

  /** Открыть бургер меню */
  showBurgerMenu = false;

  /** При нажатии "Еще" открыть dropdown, в котором скрыта часть главного меню */
  showVerticalMainMenu = false;

  sticky = true;

  showCode = false;

  showFirmName = false;

  showStars = false;

  openedDropdown = null;

  isCookieBannerVisible = false;

  cookieBannerPopup = null;

  constructor(rootStore) {
    makeObservable(this, {
      showMenu: observable,
      searchOverlay: observable,
      showSubmenu: observable,
      showUserMenuWhenSticky: observable,
      showBurgerMenu: observable,
      showVerticalMainMenu: observable,
      sticky: observable,
      showCode: observable,
      showFirmName: observable,
      showStars: observable,
      openedDropdown: observable,
      isCookieBannerVisible: observable,

      setShowCode: action,
      setShowStars: action,
      setShowFirmName: action,
      setMenuVisibleWhenSticky: action,
      toogleUserMenuVisibilityWhenSubmenuStickyChange: action,
      setOverlay: action,
      toggleUserMenu: action,
      toggleSubmenu: action,
      toggleBurgerMenu: action,
      toggleMainMenuHiddenPart: action,
      handleStickyChange: action,
      initCookieBanner: action,
      showCookieBanner: action,
      hideCookieBanner: action,
      closeDropdown: action,
      toggleDropdown: action,

      isMainPage: computed,
    });

    this.rootStore = rootStore;

    this.matchMediaUserMenuOverlay =
      !!window.matchMedia && window.matchMedia("(max-width: 600px)");
    this.matchMediaShowUserMenuWhenSticky =
      !!window.matchMedia && window.matchMedia("(max-width: 1023px)");
  }

  get isMainPage() {
    if (window?.location?.href) {
      const mainPageUrl = `https://${this.rootStore.baseUrl}/`;

      const currentUrl = new URL(window.location.href);
      const targetUrl = new URL(mainPageUrl);

      return (
        currentUrl.origin === targetUrl.origin &&
        currentUrl.pathname === targetUrl.pathname
      );
    }

    return false;
  }

  initCookieBanner = async (isUser) => {
    const { enabler, popupController } = this.rootStore;

    let shouldShowPopup = false;

    if (isUser) {
      const feature = await enabler.getFeatureById(COOKIE_BANNER_FEATURE_ID);

      if (feature && feature.is_enabled && !feature.is_viewed) {
        shouldShowPopup = true;
      } else if (feature && feature.is_enabled && feature.is_viewed) {
        setCookie("wasCookieAccepted", "true", {
          domain: window.location.hostname.replace(/.*(.ati\.s.*)/g, "$1"),
          expires: 365,
        });
      }
    } else {
      const cookie = getCookie("wasCookieAccepted");

      shouldShowPopup = cookie !== "true";
    }

    if (shouldShowPopup) {
      const newPopupElement = new PopupElement({
        name: "cookieBanner",
        eventType: "internal",
        priority: "important",
        position: "bottomRight",
        openCallback: this.showCookieBanner,
        custom: true,
      });

      this.cookieBannerPopup = newPopupElement;

      setTimeout(() => {
        popupController.addPopupToQueue(newPopupElement);
      }, 1000);
    }
  };

  showCookieBanner = () => {
    this.isCookieBannerVisible = true;
  };

  hideCookieBanner = () => {
    const { enabler, popupController, profile } = this.rootStore;

    this.isCookieBannerVisible = false;

    // устанавливаем куки
    setCookie("wasCookieAccepted", "true", {
      domain: window.location.hostname.replace(/.*(.ati\.s.*)/g, "$1"),
      expires: 365,
    });

    if (profile.isLoggedIn) {
      enabler.markAsViewed(COOKIE_BANNER_FEATURE_ID);
    }

    if (this.cookieBannerPopup) {
      popupController.handlePopupClose(this.cookieBannerPopup);
    }
  };

  toggleDropdown = (name) => {
    if (name === this.openedDropdown) {
      this.openedDropdown = null;
    } else {
      this.openedDropdown = name;
    }
  };

  closeDropdown = () => {
    this.openedDropdown = null;
  };

  setShowCode = (newValue) => {
    this.showCode = newValue;
  };

  setShowStars = (newValue) => {
    this.showStars = newValue;
  };

  setShowFirmName = (newValue) => {
    this.showFirmName = newValue;
  };

  addMatchMediaEventListener = (matchMedia, func) => {
    if (matchMedia.addEventListener) {
      matchMedia.addEventListener("change", func);
    } else {
      matchMedia.addListener(func);
    }
  };

  removeMatchMediaEventListener = (matchMedia, func) => {
    if (matchMedia.removeEventListener) {
      matchMedia.removeEventListener("change", func);
    } else {
      matchMedia.removeListener(func);
    }
  };

  setBodyOverflow = (value) => {
    if (document) {
      if (value) {
        document.body.style.overflow = "hidden";
      } else {
        document.body.style.overflow = "";
      }
    }
  };

  subscribeToMatchMedia = (event) => {
    this.setOverlay(event.matches);
    this.setBodyOverflow(event.matches);
  };

  toogleOverlayOnMobile = (matchMedia, trigger) => {
    if (trigger) {
      this.addMatchMediaEventListener(matchMedia, this.subscribeToMatchMedia);

      setTimeout(() => {
        runInAction(() => {
          if (matchMedia.matches) {
            this.setOverlay(true);
            this.setBodyOverflow(true);
          }
        });
      }, 0);
    }

    if (!trigger) {
      if (matchMedia.matches) {
        this.setOverlay(false);
        this.setBodyOverflow(false);
      }

      this.removeMatchMediaEventListener(
        matchMedia,
        this.subscribeToMatchMedia
      );
    }
  };

  setMenuVisibleWhenSticky = (event) => {
    this.showUserMenuWhenSticky = event.matches;
  };

  toogleUserMenuVisibilityWhenSubmenuStickyChange = (matchMedia, trigger) => {
    if (trigger) {
      this.addMatchMediaEventListener(
        matchMedia,
        this.setMenuVisibleWhenSticky
      );

      setTimeout(() => {
        runInAction(() => {
          if (matchMedia.matches) {
            this.showUserMenuWhenSticky = matchMedia.matches;
          }
        });
      }, 0);
    }

    if (!trigger) {
      this.removeMatchMediaEventListener(
        matchMedia,
        this.setMenuVisibleWhenSticky
      );
    }
  };

  setOverlay = (overlayState) => {
    this.searchOverlay = overlayState;
  };

  /** Переключить видимость меню */
  toggleUserMenu = (showMenu) => {
    this.showMenu = typeof showMenu === "boolean" ? showMenu : !this.showMenu;

    this.toggleDropdown("profile");

    // this.toogleOverlayOnMobile(this.matchMediaUserMenuOverlay, this.showMenu);

    // this.toogleUserMenuVisibilityWhenSubmenuStickyChange(
    //   this.matchMediaShowUserMenuWhenSticky,
    //   this.showMenu
    // );
  };

  /** Переключить видимость подменю */
  toggleSubmenu = () => {
    this.showSubmenu = !this.showSubmenu;
  };

  /** Переключить видимость бургер меню */
  toggleBurgerMenu = () => {
    this.showBurgerMenu = !this.showBurgerMenu;
  };

  /** Переключить видимость dropdown, в котором скрыта часть главного меню */
  toggleMainMenuHiddenPart = () => {
    this.showVerticalMainMenu = !this.showVerticalMainMenu;
  };

  /**
   * Меняет состояние sticky
   */
  handleStickyChange = (isNotSticky) => {
    const { notifications } = this.rootStore;

    this.sticky = !isNotSticky;

    // TODO: убрать после исчезновения старой шапки
    notifications.onNotificationsDropdownToggle(false);

    notifications.setShowNotificationDropdown(false);

    if (isNotSticky) {
      if (this.openedDropdown !== "all-services") {
        this.localeOpened = this.openedDropdown;
        this.closeDropdown();
      }
    } else if (this.localeOpened) {
      this.toggleDropdown(this.localeOpened);
      this.localeOpened = "";
    }
  };

  handleYourLoadsClick = () => {
    const { baseUrl } = this.rootStore;

    this.rootStore.profile.onLoginOpen({
      next: `https://loads.${baseUrl}/published`,
      additionalQueryParams: this.isMainPage ? MAIN_PAGE_QUERY_PARAM : "",
    });
  };

  handleYourTrucksClick = () => {
    const { baseUrl } = this.rootStore;

    this.rootStore.profile.onLoginOpen({
      next: `https://trucks.${baseUrl}/published`,
      additionalQueryParams: this.isMainPage ? MAIN_PAGE_QUERY_PARAM : "",
    });
  };
}

export default Header;
